/**
 * useBatchReview - Hook générique pour la review batch (dépenses ET factures)
 *
 * Extrait la logique commune : state items, stats, validate/reject/edit actions.
 * Le composant parent fournit la fonction d'extraction des données OCR.
 */

import { useState, useCallback, useMemo } from 'react';
import type { OCRResultItem } from './useBatchOCR';

type ItemStatus = 'pending' | 'validated' | 'editing' | 'rejected';

export interface ReviewItem<T> {
  ocrResult: OCRResultItem;
  status: ItemStatus;
  editedData: T;
}

export interface BatchReviewStats {
  total: number;
  validated: number;
  pending: number;
  rejected: number;
}

export function useBatchReview<T>(
  results: OCRResultItem[],
  extractData: (ocrData: Record<string, unknown> | null, filename: string) => T
) {
  const [items, setItems] = useState<ReviewItem<T>[]>(() =>
    results.map((r) => ({
      ocrResult: r,
      status: (r.success ? 'pending' : 'rejected') as ItemStatus,
      editedData: extractData(r.data, r.filename),
    }))
  );

  const stats: BatchReviewStats = useMemo(() => ({
    total: items.length,
    validated: items.filter((i) => i.status === 'validated').length,
    pending: items.filter((i) => i.status === 'pending').length,
    rejected: items.filter((i) => i.status === 'rejected').length,
  }), [items]);

  const updateItem = useCallback((index: number, updates: Partial<ReviewItem<T>>) => {
    setItems((prev) => prev.map((item, i) => i === index ? { ...item, ...updates } : item));
  }, []);

  const updateField = useCallback((index: number, field: keyof T, value: string | number) => {
    setItems((prev) => prev.map((item, i) =>
      i === index
        ? { ...item, editedData: { ...item.editedData, [field]: value } }
        : item
    ));
  }, []);

  const validateItem = useCallback((index: number) => {
    updateItem(index, { status: 'validated' });
  }, [updateItem]);

  const rejectItem = useCallback((index: number) => {
    updateItem(index, { status: 'rejected' });
  }, [updateItem]);

  const editItem = useCallback((index: number) => {
    updateItem(index, { status: 'editing' });
  }, [updateItem]);

  const confirmEdit = useCallback((index: number) => {
    updateItem(index, { status: 'validated' });
  }, [updateItem]);

  const cancelEdit = useCallback((index: number) => {
    updateItem(index, { status: 'pending' });
  }, [updateItem]);

  const validateAllPending = useCallback(() => {
    setItems((prev) => prev.map((item) =>
      item.status === 'pending' ? { ...item, status: 'validated' as ItemStatus } : item
    ));
  }, []);

  const getValidatedItems = useCallback((): T[] => {
    return items.filter((i) => i.status === 'validated').map((i) => i.editedData);
  }, [items]);

  return {
    items,
    stats,
    updateItem,
    updateField,
    validateItem,
    rejectItem,
    editItem,
    confirmEdit,
    cancelEdit,
    validateAllPending,
    getValidatedItems,
  };
}
